import { alpha, Box, Button, Card, CardContent, CardHeader, Chip, Grid, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import ReplayIcon from '@mui/icons-material/Replay';
import { useGetOne, ShowBase, useShowContext, ListControllerProps, RecordMap, useGetMainList, ListContextProvider } from "ra-core";
import { FC, ReactElement, useMemo, useState } from "react";
import { Content } from "~/components/content/Content";
import { FunctionField } from "~/components/field";
import { SearchAndAddProps, SearchAndAdd } from "~/components/SearchAndAdd";
import ClientsTable from "~/components/settings/clients/ClientsTable";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import WithSave from "~/controller/WithSave";
import { RecPatch } from "~/dataProvider/tools";
import { FileEncryptionTemplateDto, FileTemplateClientAccessPolicyResponseDto, ClientManageDto, FileTemplateClientAccessPolicyDto, FileTemplateKind } from "~/gen/api/encrypt";
import { ClientManageDto as ClientDto, ClientStatus } from "~/gen/api/user";
import { OdResourcePageProps } from "~/types/resource";
import { decodeHashid } from "~/utils/hashid";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { useResourceRegister } from "~/utils/resourceRegister";
import { BackToTopButton } from "../layout/Layout";
import { useUserTimeFormat } from "~/utils/userTimeFormat";
import Edit from "~/controller/Edit";


const clientsDriverRes = 'settings-clients-driver-encrypttool'

const FileTemplateAssignClients: FC<OdResourcePageProps> = (props) => {
  useResourceRegister({ name: clientsDriverRes })
  
  const { match, resource } = props
  const id = decodeHashid(match?.params['tmplId'] ?? '')

  const { data: templateInfo } = useGetOne<FileEncryptionTemplateDto&RecPatch>('file-encrypt-template', id)
  
  return (
    <ShowBase resource={resource} id={id} basePath="">
      <FileTemplateAssignClientsBody id={id} templateInfo={templateInfo} {...props} />
    </ShowBase>
  )
}

const FileTemplateAssignClientsBody: FC<OdResourcePageProps&RecPatch&{templateInfo?:FileEncryptionTemplateDto}> = ({
  id,
  templateInfo,
  options,
  resource,
}) => {
  const { record: assignmentDto, refetch } = useShowContext<FileTemplateClientAccessPolicyResponseDto&RecPatch>()

  const [states, setStates] = useState({
    term: '',
    stage: {
      removed: new Set<string>(),
      added: [] as ClientDto[],
    }
  })

  const { data: clientsOptionData, ids: clientsOptionIds } = useGetMainList<ClientDto&RecPatch>(clientsDriverRes, pages(), sorts(), {status: ClientStatus.Enabled}, { action: 'CUSTOM_DATA_QUERY' })
  
  const {assigned, assignedIds, clientsOptionIdsRemained} = useMemo(() => {
    const assignedSorted = (assignmentDto?.clients ?? []).sort((a, b) => (a.clientName ?? '') < (b.clientName ?? '') ? -1 : 1)
    const assignedIds = assignedSorted.map(c => c.clientId!)
    const assigned : RecordMap<ClientManageDto & RecPatch> = assignedSorted.reduce((acc, c) => {
      acc[c.clientId!] = c
      return acc
    }, {} as any)
    const excludes = new Set<string>(assignedIds)
    const clientsOptionIdsRemained = ((clientsOptionIds ?? []) as string[]).filter(id => !excludes.has(id))
    return {assigned, assignedIds, clientsOptionIdsRemained}
  }, [assignmentDto?.clients, clientsOptionIds])

  const clientsOptions = useMemo(() => {
    const excludes = states.stage.added.map(c => c.clientId!)
    return [...(clientsOptionIdsRemained).filter(id => excludes.indexOf(id) === -1), ...states.stage.removed]
      .flatMap(id => {
        const c = clientsOptionData && clientsOptionData[id]
        return c ? [c] : []
      }).sort((a, b) => (a.clientName ?? '') < (b.clientName ?? '') ? -1 : 1)
  }, [clientsOptionIdsRemained, clientsOptionData, states.stage])
  
  const assignLsCtx : Partial<ListControllerProps<ClientManageDto&RecPatch>> = useMemo(() => {
    if (states.stage.added.length === 0) {
      return { data: assigned, ids: assignedIds, currentSort: { field: 'id', order: 'ASC' } }
    }
    const sorted = states.stage.added.sort((a, b) => (a.clientName ?? '') < (b.clientName ?? '') ? -1 : 1)
    const data = sorted.reduce((acc, d) => {
      acc[d.clientId!] = d
      return acc
    }, {...assigned} as any)
    const ids = [...sorted.map(d => d.clientId!), ...assignedIds]
    return { data, ids, currentSort: { field: 'id', order: 'ASC' } }
  }, [assigned, assignedIds, states.stage.added])
  
  const removeValue = (x:ClientManageDto) => {
    setStates(v => {
      const i = v.stage.added.findIndex((c:ClientDto) => c.clientId === x.clientId)
      if (i > -1) {
        const arr = v.stage.added
        return {...v,
          stage: {...v.stage,
            added: [...arr.slice(0, i), ...arr.slice(i + 1)]
          }
        }
      } else {
        v.stage.removed.add(x.clientId!)
        return {...v, stage: {...v.stage}}
      }
    })
  }
  const addValue = (x:ClientManageDto|ClientManageDto[]) => {
    if (x instanceof Array) {
      setStates(v => {
        const arr = [] as any[]
        for (const y of x) {
          if (v.stage.removed.has(y.clientId!)) { continue; }
          if (clientsOptionIdsRemained.every(id => id !== y.clientId) || v.stage.added.some((c:ClientDto) => c.clientId === y.clientId)) 
          { continue; }
          arr.push(y)
        }
        return arr.length > 0 ? {...v,
          stage: {...v.stage,
            added: [...arr, ...v.stage.added]
          }
        } : v
      })
    } else {
      setStates(v => {
        if (v.stage.removed.has(x.clientId!)) {
          v.stage.removed.delete(x.clientId!)
          return {...v, stage: {...v.stage}}
        } else if (clientsOptionIdsRemained.every(id => id !== x.clientId) || v.stage.added.some((c:ClientDto) => c.clientId === x.clientId)) {
          return v
        } else {
          return {...v,
            stage: {...v.stage,
              added: [x, ...v.stage.added]
            }
          }
        }
      })
    }
  }

  const handleSave = (save: (record: any, callbacks?: any) => void) => () => {
    const payload : FileTemplateClientAccessPolicyDto = {
      templateId: id,
      accessibleClientIds: ((assignLsCtx.ids ?? []) as string[]).filter(id => !states.stage.removed.has(id))
    }

    save(payload, {
      onSuccess: () => {
        refetch && refetch()
        setStates(v => ({...v, 
          stage: {
            removed: new Set<string>(),
            added: [] as ClientDto[],
          }}
        ))
      }
    })
  }

  const theme = useTheme()
  const [addedBgc, removedBgc] = 
    useMemo(() => [
      alpha(theme.palette.success.light, 0.05),
      alpha(theme.palette.error.light, 0.05),
    ], [theme])
  const rowBgAdjust = (id:string) => (div?: HTMLDivElement) => {
    if (!div) { return }
    const tr = div.closest('tr.MuiTableRow-root')
    if (!tr) { return }
    if (states.stage.removed.has(id)) {
      tr.classList.contains('removed') || tr.classList.add('removed')
    } else if (states.stage.added.some((c: ClientManageDto) => c.clientId === id)) {
      tr.classList.contains('added') || tr.classList.add('added')
    } else {
      tr.classList.contains('added') && tr.classList.remove('added')
      tr.classList.contains('removed') && tr.classList.remove('removed')
    }
  }
  const assignClientsHandlers = useDefaultSavedHandlers("Assign Clients")
  const format = useUserTimeFormat()
  
  return (
    <Content 
      pageName="Assign Clients" 
      resourceName="Assign Clients" 
      breadcrumbs={[ ...(options?.breadcrumbs ?? []), { label: 'Encryption Mappings', path: `/encryption/mappings?tab=file` } ]}
    >
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Card>
            <Box sx={{ my: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, mx: 2 }}>
                <Typography color="textPrimary" variant="h6">Template</Typography>
                <Typography variant="body1" sx={{ textTransform: 'uppercase' }} >{templateInfo?.name}</Typography>
                <Typography variant="caption" component="i"
                  sx={{ 
                    color: theme => theme.palette.mode === 'light' ? '#757575' : '#9e9e9e',
                    display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                    my: -1,
                  }} 
                >{
                    templateInfo?.kind === FileTemplateKind.CSV        ? ['CSV File', chipMap[templateInfo?.delimitingChar ?? '']] 
                  : templateInfo?.kind === FileTemplateKind.FixedWidth ? 'Fixed Width File' 
                  : templateInfo?.kind === FileTemplateKind.JsonSample ? 'Json File' 
                  : templateInfo?.kind === FileTemplateKind.EDI        ? 'EDI File' 
                  : ''
                }</Typography>
                <Typography sx={{ color: theme => theme.palette.mode === 'light' ? '#757575' : '#9e9e9e'}} variant="caption" component="i">
                  Upload at: {format(templateInfo?.uploadTime || 0, "PPp")}
                </Typography>
              </Stack>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Assign Clients" 
              action={(
                (states.stage.added.length > 0 || states.stage.removed.size > 0) &&
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2">Edits:</Typography>
                  {!!states.stage.added.length && <Typography variant="caption" color="success.main">+ {states.stage.added.length}</Typography>}
                  {!!states.stage.removed.size && <Typography variant="caption" color="error.main">- {states.stage.removed.size}</Typography>}
                  <Edit resource={resource} basePath="" mutationMode="pessimistic" id={id} {...assignClientsHandlers}>
                    <WithSave 
                      render={({save, saving}) => (
                        <>
                          <Button
                            size="small"
                            color="primary"
                            variant="outlined"
                            disabled={saving}
                            onClick={() => setStates(v => ({...v, 
                              stage: {
                                removed: new Set<string>(),
                                added: [] as ClientDto[],
                              }}
                            ))}
                          >
                            Discard
                          </Button>
                          <Button
                            size="small"
                            color="primary"
                            variant="contained"
                            disabled={saving}
                            onClick={handleSave(save)}
                          >
                            Save
                          </Button>
                        </>)}
                    />
                  </Edit>
                </Stack>
              )}
            />
            <CardContent>
              <Box marginBottom={2}>
                <SearchAndAddClient term={states.term} setTerm={(term) => setStates(v => ({...v, term}))}
                  options={clientsOptions}
                  getOptionLabel={(x)=>x.clientName!}
                  onAdd={addValue}
                  liBoxSx={{ whiteSpace: 'break-spaces', wordBreak: 'break-all'}}
                />
              </Box>
              <ListContextProvider value={assignLsCtx}>
                <ClientsTable 
                  sx={{ 
                    '& th' : { py: 2 },
                    '& td' : { py: 2 },
                    '& tr': {
                      '&.added':   { bgcolor: addedBgc },
                      '&.removed': { bgcolor: removedBgc },
                    }
                  }}
                  ActionField={
                    <FunctionField label="Action" sortable={false}
                      render={(d?: ClientManageDto & RecPatch) => (
                        <Box ref={rowBgAdjust(d?.clientId!)}>
                          {
                            !states.stage.removed.has(d?.clientId!) 
                            ? <Tooltip title="Remove Assignment">
                                <IconButton color="primary" onClick={() => removeValue(d!)}>
                                  <ClearIcon />
                                </IconButton>
                              </Tooltip> 
                            : <Tooltip title="Restore Assignment">
                                <IconButton color="primary" onClick={() => addValue(d!)}>
                                  <ReplayIcon />
                                </IconButton>
                              </Tooltip>
                          }
                        </Box>
                      )} 
                    />}
                />
              </ListContextProvider>
            </CardContent>
          </Card>
          <BackToTopButton />
        </Grid>
      </Grid>
    </Content>
  )
}

export default FileTemplateAssignClients;

const SearchAndAddClient : FC<SearchAndAddProps<ClientDto>> = SearchAndAdd

const chipMap : Record<string, ReactElement> = {
  ',':  <Chip size="small" label={<><Typography variant="body1" component="span" sx={{fontWeight:700}}>,</Typography></>} />,
  ';':  <Chip size="small" label={<><Typography variant="body1" component="span" sx={{fontWeight:700}}>;</Typography></>} />,
  '\t': <Chip size="small" label={<>tab ( <Typography variant="body1" component="span" sx={{fontWeight:700}}>\t</Typography> )</>} />,
}
